/* eslint-disable no-param-reassign */

import { createSlice } from 'helpers/createSlice';
import { factoryExtraSelectors } from 'helpers/slice';
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import {
  ActionWithPayload,
  ApiError,
  ApiMakerSelectors,
  ApiReducers,
  ApiState,
  ApiStateData,
  CreateApiSliceArgs,
  CreateApiSliceReturn,
  CreateApiSliceReturnSelectors,
  MakerSelectors,
} from 'types';

export function createApiSlice<
  Data,
  Payload = undefined,
  MSelectors extends MakerSelectors<ApiStateData<Data>> | undefined = undefined,
  SagaServices = undefined
>({
  nestedPath,
  name,
  tryGenerator,
  extraSelectors,
}: CreateApiSliceArgs<Data, Payload, MSelectors, SagaServices>): CreateApiSliceReturn<
  Data,
  Payload,
  MSelectors,
  SagaServices
> {
  const initialState: ApiState<Data> = {
    rs: 'NOT_STARTED',
    data: null,
    error: null,
  };

  const { actions, reducer, selectors } = createSlice<
    ApiState<Data>,
    ApiReducers<Data, Payload>,
    ApiMakerSelectors<Data>
  >({
    nestedPath,
    name,
    initialState,
    reducers: {
      reset: () => initialState,
      call: () => {}, // eslint-disable-line
      busy: (state) => {
        state.rs = 'BUSY';
        state.error = null;
      },
      success: (_, { payload }) => ({
        rs: 'SUCCESS',
        data: payload,
        error: null,
      }),
      completed: (state) => {
        state.rs = 'NOT_STARTED';
      },
      failed: (_, { payload }) => ({
        rs: 'FAILED',
        error: payload,
        data: null,
      }),
    },
    extraSelectors: {
      getIsNotStarted: ({ rs }) => rs === 'NOT_STARTED',
      getIsBusy: ({ rs }) => rs === 'BUSY',
      getIsSuccess: ({ rs }) => rs === 'SUCCESS',
      getIsFailed: ({ rs }) => rs === 'FAILED',
      getData: ({ data }) => data,
      getError: ({ error }) => error,
    },
  });

  // Sagas
  function* SagaCall(
    sagaServices: SagaServices,
    action: ActionWithPayload<Payload>
  ): Generator<unknown, void> {
    try {
      yield put(actions.busy());
      const response = yield call(tryGenerator, sagaServices, action);
      yield put(actions.success(response));
      yield delay(10);
      yield put(actions.completed());
    } catch (e) {
      yield put(actions.failed(e as ApiError));
    }
  }

  function* sagas(sagaServices: SagaServices) {
    yield takeLatest(actions.call.type, SagaCall, sagaServices);
  }

  const customSelectors = factoryExtraSelectors<ApiStateData<Data>, MSelectors>(
    selectors.getData,
    extraSelectors
  );

  const allSelectors = {
    ...selectors,
    ...(customSelectors || {}),
  } as CreateApiSliceReturnSelectors<Data, MSelectors>;

  return {
    reducer,
    name,
    selectors: allSelectors,
    actions,
    sagas,
  };
}
